import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const BackgroundBlobs = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none z-[-1]">
    <div className="absolute top-[-5%] right-[-10%] w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] rounded-full bg-orange-400 opacity-20 blur-3xl" />
    <div className="absolute top-[50%] left-[-15%] w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] rounded-full bg-red-400 opacity-10 blur-3xl transform -translate-y-1/2" />
  </div>
);

const Section = ({ title, children }) => (
  <section>
    <h3 className="text-xl font-bold text-slate-800 mb-3 flex items-center gap-2">
      <span className="w-2 h-2 rounded-full bg-primary block" />
      {title}
    </h3>
    {children}
  </section>
);

const Policy = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    // Override global overflow so page is fully scrollable
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      setShowScrollTop(y > 300);
    };
    window.addEventListener('scroll', onScroll, true);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, []);

  return (
    <div dir="rtl" className="bg-background text-slate-800 font-sans overflow-x-hidden max-w-[100vw] selection:bg-primary selection:text-white relative min-h-screen flex flex-col">
      <BackgroundBlobs />

      {showScrollTop && (
        <button onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); document.documentElement.scrollTo({ top: 0, behavior: 'smooth' }); document.body.scrollTo({ top: 0, behavior: 'smooth' }); }}
          className="fixed bottom-6 right-6 z-50 p-3 rounded-full bg-slate-800 text-white shadow-xl hover:bg-primary transition-all">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>
        </button>
      )}

      <nav className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex justify-between items-center relative z-10">
        <div className="flex items-center select-none" dir="ltr"><span className="font-logo font-light text-2xl sm:text-3xl text-slate-800 tracking-wide">BrokersConnect</span></div>
        <div className="flex items-center gap-3">
          <Link to="/signup" className="px-5 py-2 text-sm sm:text-base rounded-full border-2 border-slate-200 text-slate-700 font-bold hover:border-primary hover:text-primary transition-all bg-white/50 backdrop-blur-sm flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            חזור להרשמה
          </Link>
        </div>
      </nav>

      <div className="flex-1 flex flex-col items-center px-4 sm:px-6 lg:px-8 w-full relative z-10 pb-12 sm:pb-20 pt-4">
        <div className="w-full max-w-4xl relative">
          <div className="absolute inset-0 bg-primary opacity-5 transform translate-y-3 translate-x-3 rounded-3xl" />
          <div className="relative bg-surface p-6 sm:p-10 lg:p-12 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100">
            <div className="border-b border-slate-200 pb-6 mb-8 text-center sm:text-right">
              <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-3">תנאי שימוש</h1>
              <p className="text-base text-slate-600">ברוכים הבאים לאתר ברוקרס קונקט! לפני השימוש באתר, אנא קרא את תנאי השימוש בקפידה. תנאים אלה מחייבים את כל המשתמשים באתר.</p>
            </div>

            <div className="space-y-8 text-slate-600 leading-relaxed text-sm sm:text-base">
              <Section title="השימוש באתר">
                <ul className="list-disc list-inside space-y-1.5 pr-2">
                  <li>כניסה לאתר ו/או שימוש בשירותיו מהווה הסכמה מפורשת לתנאי השימוש.</li>
                  <li>תנאי השימוש עשויים להשתנות מעת לעת, ועל כן מומלץ לעיין בהם באופן קבוע.</li>
                  <li>תנאי השימוש מנוסחים בלשון זכר מטעמי נוחות בלבד.</li>
                  <li>חלוקת התנאים לסעיפים וכותרות נועדה לנוחות בלבד ואינה משפיעה על פרשנותם.</li>
                </ul>
              </Section>
              <Section title="תנאים נוספים">
                <ul className="list-disc list-inside space-y-1.5 pr-2">
                  <li>האתר רשאי לשנות את השירותים והמוצרים המוצעים בו בכל עת.</li>
                  <li>האתר רשאי לחסום את גישתך לאתר מכל סיבה שהיא לרבות עקב שימוש שאינו ראוי ומפר את תנאי האתר.</li>
                </ul>
              </Section>
              <Section title="הצהרתך">
                <ul className="list-disc list-inside space-y-1.5 pr-2">
                  <li>הנך מצהיר כי קראת את תנאי השימוש ומסכים להם במלואם.</li>
                  <li>הנך מצהיר כי תשתמש באתר באופן חוקי ותוך שמירה על כללים אלו.</li>
                </ul>
              </Section>
              <Section title="שירותי המערכת וחשבון משתמש">
                <p className="mb-2">אתר ברוקרס קונקט מספק מידע אודות שוק ההון בישראל, האתר מציע שירותים ללא תשלום ובתשלום וכן יכול לשנות מ"ללא תשלום" ל"תשלום" בכל עת ללא הודעה מוקדמת.</p>
                <p>הגישה לאתר הינה אישית באמצעות שם משתמש וסיסמה. הגולש מתחייב שלא להשתמש בקוד הסודי של אחר ושלא למסור את הקוד הסודי שלו לאחר.</p>
              </Section>
              <Section title="דיוור ופניות שיווקיות">
                <p>האתר עשוי להציע הצעות בעלות אופן מסחרי באמצעות אי-מייל, מסרונים ודרכי התקשרות נוספים העומדים לרשותו לרבות אפליקציות צד שלישי וכן שליחת מידע, דיוורים ועדכונים משתנים מעת לעת. ניתן יהיה להיגרע מרשימת התפוצה באמצעות לינק ייעודי באי מייל, ומסרונים.</p>
              </Section>
              <Section title="שינויים, דין וסמכות שיפוט">
                <p className="mb-2">לאתר שמורה הזכות לשנות את המידע המוצג בו, את מבנהו, את השירותים הניתנים בו, את תנאי השימוש ואף להפסיק את פעילותו באופן מלא או חלקי.</p>
                <p>התוכן באתר, הגישה אליו והשימוש בו כפופים לחוקי מדינת ישראל בלבד. בכל מקרה של סכסוך הנוגע לתנאי השימוש באתר, לרבות מדיניות הפרטיות, סמכות השיפוט הבלעדית תהיה לבתי המשפט המוסמכים במחוז תל אביב-יפו.</p>
              </Section>
              <Section title="קניין רוחני וזכויות יוצרים">
                <p className="mb-2">אין להעתיק ו/או לשכפל את המידע או כל חלק ממנו לצורך הפצה או פרסום בדרך כלשהי, לרבות הדפסה, צילום, צילום מסך, העברה אלקטרונית או שיתוף ברשתות חברתיות ללא הסכמה מפורשת ובכתב מהנהלת האתר.</p>
                <p className="mb-2">ברוקרס קונקט רשאית להגן על זכויותיה בנוגע לתכנים באתר בכל דרך שתמצא לנכון.</p>
                <p>השימוש באתר ובשירותים לא מעניק לגולש זכויות כלשהן באתר או בשירותים, אלא אם נצוין במפורש אחרת.</p>
              </Section>
              <Section title="פטור מאחריות והיעדר ייעוץ השקעות">
                <p className="mb-2">האתר אוסף את המידע ממספר מקורות לרבות אתרי מנהלי ההשקעות ועושה כמיטב יכולתו להציג את המידע העדכני והמדויק ביותר, מנגד האתר אינו מתחייב לשלמות במידע, נכונות המידע ואינו אחראי לכול ליקוי, טעות, שגיאה, השמטה או אי-דיוק שנפלו באתר.</p>
                <p className="mb-2">השימוש במידע באתר הינו באחריות המשתמש ולמשתמש לא תהיה כל עילה כנגד האתר במידה ונתגלו טעויות במידע.</p>
                <p className="bg-orange-50 border-r-4 border-primary p-3 rounded-l font-medium text-slate-700">המידע באתר אינו המלצה, הבעת דעה או הנעה לפעולה לרכישת ניירות ערך או עצה לנהוג בדרך זו או אחרת בכל הקשור לקבלת החלטות בתחום ההשקעות ואינו נועד להוות תחליף להתייעצות עם מומחים.</p>
              </Section>
              <Section title="קישורים, צדדים שלישיים ואבטחת מידע">
                <p className="mb-2">בעת הגלישה באתר עשויים להופיע קישורים לצדדים שלישיים. ברוקרס קונקט אינה אחראית למידע או התכנים אשר יוצגו באתרים אלו.</p>
                <p className="mb-2">האתר פועל על בסיס טכנולוגיות ענן וטכנולוגיות של צדדים שלישיים. תקלות בצדדים שלישיים עשויים לגרור לשיבוש פעילותו התקינה של האתר.</p>
                <p className="mb-2">האתר עושה מאמצים מרבים כדי לשמור על גלישה בטוחה, אך לא ניתן להבטיח באופן מוחלט את חסינות המערכות מפני ניסיונות חדירה.</p>
                <p>הגולש מתחייב לגלוש באתר בהתאם לכל דין ולא לבצע כל פעולת חדירה ו/או שיבוש.</p>
              </Section>
            </div>

            <div className="mt-10 pt-8 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-center sm:text-right">
                <p className="text-slate-800 font-bold text-lg mb-1">למידע נוסף או עזרה</p>
                <p className="text-slate-500 text-sm">ניתן ליצור איתנו קשר ישירות במייל</p>
              </div>
              <a href="mailto:info@brokersconnect.co.il" className="inline-flex items-center gap-2 px-6 py-3 bg-slate-100 hover:bg-orange-50 text-slate-800 hover:text-primary font-bold rounded-xl transition-colors border border-slate-200 hover:border-primary/30">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                info@brokersconnect.co.il
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Policy;